// client/src/components/dashboard/PrivateRoute.js
import React, { useState, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import LoadingSpinner from '../common/LoadingSpinner';
import { useAuth } from '../../context/AuthContext'; // Correct path
import axios from 'axios'; // Ensure axios is imported if used directly here

function PrivateRoute({ children }) {
    // Get setUser from AuthContext. 'user' and 'loading' from context aren't
    // strictly needed here as PrivateRoute manages its own check/loading state,
    // but you could potentially use context's 'loading' for the initial app load check.
    const { setUser } = useAuth();
    const location = useLocation();

    // Local state for managing the check within PrivateRoute
    const [loading, setLoading] = useState(true);
    const [isAuthenticated, setIsAuthenticated] = useState(false);

    useEffect(() => {
        const checkAuth = async () => {
            setLoading(true); // Start loading for this specific check
             try {
                 // Verify session with backend (replace with your actual endpoint if needed)
                 // This assumes axios is configured to send credentials/tokens automatically
                 const response = await axios.get('/api/auth/me');

                 if (response.status === 200 && response.data) {
                     // SUCCESS: User session is valid.
                     // Update the global AuthContext with user data.
                     setUser(response.data);
                     // Update local state for PrivateRoute's rendering logic.
                     setIsAuthenticated(true);
                 } else {
                     // FAILURE (Non-200 or no data): User session is invalid.
                     // Clear the global AuthContext user data.
                     setUser(null);
                     // Update local state.
                     setIsAuthenticated(false);
                 }
             } catch (error) {
                 // FAILURE (API Error, e.g., 401): User session is invalid.
                 console.error("Error checking auth in PrivateRoute:", error);
                 // Clear the global AuthContext user data.
                 setUser(null);
                 // Update local state.
                 setIsAuthenticated(false);
             } finally {
                 // Finish loading for this specific check.
                 setLoading(false);
             }
        };

        checkAuth();
     // Dependency: run check if location changes or setUser function instance changes (unlikely but safe)
    }, [location, setUser]);


    // Render based on the local loading and authentication state of this check
    if (loading) {
        return <LoadingSpinner />; // Show spinner while checking
    }

    if (!isAuthenticated) {
        // Redirect to login if check finished and user is not authenticated
        return <Navigate to="/login" replace state={{ from: location }} />;
    }

    // If check finished and user is authenticated, render the protected component
    return children; // Use 'children' directly
}

export default PrivateRoute;